import { estimateValorador } from './valoradorEngine';
import { roundValuationRange } from './valoradorEngine/calculate';
import { valoradorPublicData, VALORADOR_RANGES } from './valoradorListing';
import {
  getValoradorSteps,
  isValoradorStepComplete,
  sectorOptions,
  sectors,
  buyerSectors,
} from '../containers/ValoradorPrototypePage/valoradorSteps';
import {
  PROVINCIA_OPTIONS,
  MODALIDAD_OPTIONS,
  TIPO_OPERACION_OPTIONS,
  FACTURACION_SCALE,
  EMPLEADOS_SCALE,
  ANTIGUEDAD_SCALE,
} from '../config/configListing';
import { generateMandatoTitle } from '../containers/EditListingPage/EditListingWizard/mandatoTitle';
import { createIntl } from './reactIntl';
import messages from '../translations/es.json';

/** Only canonical selected options cross the network, never account data or free text. */
export const notificationAnswers = answers => {
  if (!answers || typeof answers !== 'object' || Array.isArray(answers)) {
    throw new Error('invalid');
  }
  const clean = {};
  for (const step of getValoradorSteps(answers)) {
    if (!isValoradorStepComplete(step, answers)) throw new Error('incomplete');
    for (const field of step.fields) {
      const keys = field.type === 'range' ? [field.name + 'Min', field.name + 'Max'] : [field.name];
      for (const key of keys) {
        const value = answers[key];
        if (
          field.type === 'range' &&
          !(
            (typeof value === 'string' && /^\d+$/.test(value)) ||
            (typeof value === 'number' && Number.isInteger(value))
          )
        ) {
          throw new Error('invalid-range');
        }
        clean[key] = value;
      }
    }
  }
  return clean;
};

/** Server calls this from the SSR bundle, sharing the exact calculation and listing contracts. */
export const buildValuationNotification = answers => {
  if (!answers || !['seller', 'buyer'].includes(answers.mode)) throw new Error('invalid');
  const clean = notificationAnswers(answers);
  if (Object.keys(answers).some(key => !Object.prototype.hasOwnProperty.call(clean, key))) {
    throw new Error('unexpected-fields');
  }
  const result = estimateValorador(clean);
  const data = valoradorPublicData(clean);
  const buyer = clean.mode === 'buyer';
  const label = (options, value) =>
    options.find(row => row.option === value)?.label || 'No disponible';
  const many = (options, values) => values.map(value => label(options, value)).join(', ');
  const money = value =>
    new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0,
    }).format(value);
  const rounded = roundValuationRange(result.equity || result.operating);
  const reason =
    result.status === 'unsupported'
      ? clean.financialProfile === 'notCovered'
        ? 'Motivo: se seleccionó «Ninguna encaja» al precisar la actividad.'
        : 'Motivo: falta un perfil de valoración válido para la actividad.'
      : result.status === 'nonpositive'
      ? `Motivo: beneficio anual ajustado no positivo (${money(result.normalizedProfit)}).`
      : result.status === 'incomplete'
      ? 'Motivo: faltan importes necesarios para calcular el beneficio ajustado.'
      : result.status === 'partial'
      ? 'Motivo: faltan datos de deuda, caja o activos para completar el precio.'
      : null;
  const pendingLabels = {
    profit: 'beneficio',
    salary: 'sueldo del propietario',
    personal: 'gastos personales',
    extraIncome: 'ingresos extraordinarios',
    extraExpense: 'gastos extraordinarios',
    rent: 'alquiler',
    debt: 'deuda',
    cash: 'tesorería',
    property: 'inmueble',
    stock: 'existencias',
    facturacion: 'facturación',
    empleados: 'empleados',
    antiguedad: 'antigüedad',
  };
  const pending = [
    ...new Set([...(result.missingNormalization || []), ...(result.missingBridge || [])]),
  ];
  const revenue = !result.operating && roundValuationRange(result.revenueReference);
  const intl = createIntl({ locale: 'es', messages });
  const provinces = [{ option: 'toda-espana', label: 'Toda España' }, ...PROVINCIA_OPTIONS];
  const title = buyer
    ? generateMandatoTitle({
        sector: data.sector,
        provincias: data.provincias,
        tiposOperacion: data.tiposOperacion,
        sectorOptions,
        provinciasOptions: provinces,
        intl,
      })
    : 'Negocio en venta / búsqueda de inversión';
  const rows = buyer
    ? [
        ['Sector', label(buyerSectors, data.sector)],
        ['Provincias', many(provinces, data.provincias)],
        ['Operación', many(TIPO_OPERACION_OPTIONS, data.tiposOperacion)],
        ['Modalidad', many(MODALIDAD_OPTIONS, data.modalidades)],
        ...VALORADOR_RANGES.map(({ name, scale, minKey, maxKey }) => [
          {
            targetAge: 'Antigüedad',
            targetTurnover: 'Facturación',
            targetEbitda: 'EBITDA',
            targetEmployees: 'Empleados',
          }[name],
          data[minKey] === scale[0].idx && data[maxKey] === scale[scale.length - 1].idx
            ? 'Cualquiera'
            : `${scale.find(row => row.idx === data[minKey]).label} / ${
                scale.find(row => row.idx === data[maxKey]).label
              }`,
        ]),
      ]
    : [
        ['Sector', label(sectors, data.sector)],
        ['Provincia', label(PROVINCIA_OPTIONS, data.provincia)],
        ['Operación', many(TIPO_OPERACION_OPTIONS, data.tipo_operacion)],
        ['Modalidad', label(MODALIDAD_OPTIONS, data.modalidad)],
        ['Facturación', label(FACTURACION_SCALE, data.facturacion)],
        ['Empleados', EMPLEADOS_SCALE.find(row => row.lowerBound === data.empleados)?.label],
        ['Antigüedad', label(ANTIGUEDAD_SCALE, data.antiguedad)],
      ];
  return [
    'Nueva valoración completada',
    `Intención: ${buyer ? 'comprar o invertir' : 'vender o buscar inversión'}`,
    rounded
      ? `${
          result.equity
            ? 'Precio orientativo de la operación'
            : 'Valor orientativo del negocio (precio de operación incompleto)'
        }: ${money(rounded.low)} – ${money(rounded.high)}`
      : 'No se ha podido calcular un rango con las respuestas facilitadas.',
    ...(result.profile?.comparable
      ? [
          'Estimación aproximada basada en una actividad comparable; requiere revisión específica del negocio.',
        ]
      : []),
    ...(reason ? [reason] : []),
    ...(pending.length
      ? [
          'Resultado con datos pendientes.',
          `Datos pendientes: ${pending.map(key => pendingLabels[key] || key).join(', ')}.`,
        ]
      : []),
    ...(revenue
      ? [
          `Referencia por facturación (no es el precio de la operación): ${money(
            revenue.low
          )} – ${money(revenue.high)}`,
        ]
      : []),
    '',
    buyer ? 'Borrador de mandato de compra' : 'Borrador de anuncio de venta',
    title,
    ...rows.map(([key, value]) => `${key}: ${value}`),
    '',
    'No publicado. Resumen al terminar el recorrido; puede cambiar antes de publicar.',
  ].join('\n');
};
